import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import { healthNotices, healthOf, isSectionUnavailable } from "../admin/health";
import {
  AdAvatar,
  Empty,
  Pill,
  Progress,
  SortableTh,
  monthYear,
  type HealthState,
  type TableSort,
} from "../admin/ui";
import { api, type AdminClientListItem } from "../services/api";

type Filter = "all" | "active" | "setup" | "inactive";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "setup", label: "Setup incomplete" },
  { key: "inactive", label: "Inactive" },
];

type SortKey = "org" | "setup" | "users" | "signin" | "tickets" | "since" | "status";

/** Sign-in ordered by how much attention it wants, not alphabetically, so one
 *  click on the column puts the customers nobody can sign in to on top. */
const SIGNIN_RANK: Record<HealthState, number> = { todo: 0, warn: 1, ok: 2 };

/**
 * How each column reads a row, and which way its FIRST click points.
 *
 * The exceptions lead: least-finished setup, worst sign-in and disabled portals
 * are the rows worth opening, so those columns open ascending, while the counts
 * and the date open at the largest. A second click reverses, a third returns to
 * the server's A-Z order.
 */
const COLUMNS: Record<
  SortKey,
  { value: (c: AdminClientListItem) => number | string; firstDir: "asc" | "desc" }
> = {
  org: { value: (c) => c.name, firstDir: "asc" },
  setup: {
    // The ratio, not `done`: the totals move together across the list today,
    // but a section that only counts for some customers must not read as
    // further behind than one that counts for all of them.
    value: (c) => {
      // Unknown health is not "least finished": above any real ratio (max 1),
      // so those rows stay out of the worst-first group the column opens on.
      if (!c.setup_health) return 2;
      const h = healthOf(c);
      return h.total > 0 ? h.done / h.total : 0;
    },
    firstDir: "asc",
  },
  users: { value: (c) => c.user_count, firstDir: "desc" },
  signin: {
    value: (c) => {
      const h = healthOf(c);
      // Unknown is not "nobody can sign in". Keep those rows out of the
      // worst-first group the column opens on.
      if (!c.setup_health || isSectionUnavailable(h, "sso")) return 3;
      return SIGNIN_RANK[h.sso];
    },
    firstDir: "asc",
  },
  tickets: { value: (c) => c.tickets_30d ?? -1, firstDir: "desc" },
  since: { value: (c) => new Date(c.created_at).getTime(), firstDir: "desc" },
  status: { value: (c) => (c.is_active ? 1 : 0), firstDir: "asc" },
};

function compareValues(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/**
 * Customers list.
 *
 * One row per organisation, with the onboarding progress bar and sign-in state
 * read straight off the server-computed setup health. The whole row opens the
 * customer page — everything for one customer lives there, so there is no
 * per-row action menu.
 *
 * Search, the chips and the column sort are all local to the one fetch of the
 * list, so they compose: sorting narrows nothing, it just reorders whatever the
 * chip and the search box have left.
 */
export function AdminClients() {
  const navigate = useNavigate();
  const removedCustomer = (useLocation().state as { removedCustomer?: string } | null)
    ?.removedCustomer;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<TableSort<SortKey> | null>(null);

  // One fetch of the whole list with health; filtering and searching are local
  // so the chips can carry live counts and the progress bars never re-flash.
  const { data, isLoading, error } = useSWR<AdminClientListItem[]>(
    ["/api/admin/clients", "health"],
    () => api.admin.clients.list({ include: "health" }),
    { keepPreviousData: true },
  );

  const all = useMemo(() => data ?? [], [data]);

  const counts = useMemo(
    () => ({
      all: all.length,
      active: all.filter((c) => c.is_active).length,
      setup: all.filter(
        (c) =>
          c.is_active &&
          c.setup_health != null &&
          healthOf(c).done < healthOf(c).total,
      ).length,
      inactive: all.filter((c) => !c.is_active).length,
    }),
    [all],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      const h = healthOf(c);
      const passesFilter =
        filter === "all"
          ? true
          : filter === "active"
            ? c.is_active
            : filter === "inactive"
              ? !c.is_active
              : c.is_active && c.setup_health != null && h.done < h.total;
      if (!passesFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.slug.includes(q) ||
        String(c.autotask_company_id ?? "").includes(q)
      );
    });
  }, [all, filter, search]);

  const notices = useMemo(() => healthNotices(all), [all]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const read = COLUMNS[sort.key].value;
    const sign = sort.dir === "asc" ? 1 : -1;
    // Name breaks every tie, and always ascending, so the many customers who
    // share a value (0 tickets, 6/6 setup) stay in the order the reader
    // already knows rather than shuffling on each re-render.
    return rows
      .slice()
      .sort(
        (a, b) => sign * compareValues(read(a), read(b)) || a.name.localeCompare(b.name),
      );
  }, [rows, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => {
      if (s?.key !== key) return { key, dir: COLUMNS[key].firstDir };
      // Reverse, then back to the default order: nobody gets stuck in an
      // ordering they cannot undo without reloading the page.
      return s.dir === COLUMNS[key].firstDir
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : null;
    });
  }

  return (
    <div className="rf-wrap">
      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">Admin</div>
          <h1 className="rf-h1">Customers</h1>
          <p className="rf-sub">
            Every organisation on the portal. Open one to configure it — everything for
            that customer lives in one place.
          </p>
        </div>
        <div className="grp">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/clients/new")}
          >
            <Icon name="plus" size={15} /> New customer
          </button>
        </div>
      </div>

      {removedCustomer && (
        <div className="form-success" style={{ marginBottom: 16 }}>
          {removedCustomer} has been removed from the portal. Their tickets remain in
          Autotask.
        </div>
      )}

      <div className="ad-filters">
        <div className="rf-search">
          <Icon name="search" />
          <input
            type="search"
            placeholder="Search customers"
            value={search}
            aria-label="Search customers"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="rf-chiprow">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`rf-chip${filter === f.key ? " active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} <span style={{ opacity: 0.6 }}>{counts[f.key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="ad-card">
        {error ? (
          <div className="form-error" style={{ margin: 18 }}>
            The customer list could not be loaded. Refresh the page to try again.
          </div>
        ) : isLoading ? (
          <div style={{ padding: 18 }}>Loading customers…</div>
        ) : sorted.length === 0 ? (
          <Empty title="No customers match" blurb="Try another filter or search." />
        ) : (
          <>
            {notices.banner.map((message) => (
              <div key={message} className="form-error" style={{ margin: 18 }}>
                {message}
              </div>
            ))}
            {notices.rows.map((row) => (
              <div key={row.id} className="form-error" style={{ margin: "0 18px 12px" }}>
                {row.name}: {row.message}
              </div>
            ))}
          <div className="ad-tablewrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <SortableTh label="Organisation" sortKey="org" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Setup" sortKey="setup" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Users" sortKey="users" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Sign-in" sortKey="signin" sort={sort} onSort={toggleSort} />
                  <SortableTh
                    label="Tickets · 30d"
                    sortKey="tickets"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableTh label="Since" sortKey="since" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const h = healthOf(c);
                  return (
                    <tr
                      key={c.id}
                      className="clickable"
                      onClick={() => navigate(`/admin/clients/${c.id}`)}
                    >
                      <td>
                        <span className="who">
                          <AdAvatar name={c.name} />
                          <span>
                            <span className="n">{c.name}</span>
                            <br />
                            <span className="e mono">
                              {c.autotask_company_id != null
                                ? `AT ${c.autotask_company_id}`
                                : "Autotask not linked"}{" "}
                              · /{c.slug}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        {c.setup_health ? (
                          <Progress done={h.done} total={h.total} />
                        ) : (
                          <span className="s">Unavailable</span>
                        )}
                      </td>
                      <td>{c.user_count}</td>
                      <td>
                        {!c.setup_health || isSectionUnavailable(h, "sso") ? (
                          <Pill kind="off">Unavailable</Pill>
                        ) : h.sso === "ok" ? (
                          <Pill kind="ok">SSO verified</Pill>
                        ) : h.sso === "warn" ? (
                          <Pill kind="warn">Unverified</Pill>
                        ) : (
                          <Pill kind="off">Not connected</Pill>
                        )}
                      </td>
                      <td>
                        {c.tickets_30d == null && c.health_error
                          ? "Unavailable"
                          : (c.tickets_30d ?? 0)}
                      </td>
                      <td className="mono">{monthYear(c.created_at)}</td>
                      <td>
                        {c.is_active ? (
                          <Pill kind="ok">Active</Pill>
                        ) : (
                          <Pill kind="off">Inactive</Pill>
                        )}
                      </td>
                      <td className="r">
                        <span style={{ color: "var(--iq-text-disabled)", display: "inline-flex" }}>
                          <Icon name="chevRight" size={16} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
